import { type FC } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { FormBuilder } from "@/shared/components/Form";
import type { Schema } from "@/shared/components/Form/types";
import { useUpdatePlatformStreamingSettingsMutation } from "@/admin/entities/platforms";
import type { PlatformShowQueryType } from "@/types/graphql/admin.generated";

const schema: Schema = {
  accountId: {
    type: "text",
    label: "Cloudflare Account ID",
    required: true,
    xs: 12,
    fullWidth: true,
  },
  apiToken: {
    type: "password",
    label: "API Token (Stream: Edit)",
    required: true,
    xs: 12,
    fullWidth: true,
  },
};

interface PlatformStreamingSettingsCardProps {
  platformId: string;
  streamingSettings: NonNullable<
    PlatformShowQueryType["platform"]
  >["streamingSettings"];
  streamingAvailable: NonNullable<
    PlatformShowQueryType["platform"]
  >["streamingAvailable"];
}

export const PlatformStreamingSettingsCard: FC<
  PlatformStreamingSettingsCardProps
> = ({ platformId, streamingSettings, streamingAvailable }) => {
  const { enqueueSnackbar } = useSnackbar();
  const usesDefaultCredentials = Boolean(
    !streamingSettings?.connectedAt && streamingAvailable
  );

  const [updatePlatformStreamingSettings, { loading }] =
    useUpdatePlatformStreamingSettingsMutation({
      onCompleted: () => {
        enqueueSnackbar("Cloudflare Stream подключён", {
          variant: "success",
        });
      },
      onError: (error: Error) => {
        enqueueSnackbar(
          error.message.includes("INVALID_CLOUDFLARE_TOKEN")
            ? "Неверный API token — проверьте права Stream: Edit"
            : error.message,
          { variant: "error" }
        );
      },
    });

  return (
    <Card>
      <CardHeader
        title="Cloudflare Stream"
        action={
          streamingSettings?.connectedAt ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" color="success" label="Подключено" />
              <Chip
                size="small"
                variant="outlined"
                label={streamingSettings.apiTokenMask}
              />
            </Stack>
          ) : usesDefaultCredentials ? (
            <Chip
              size="small"
              color="info"
              label="Работает на ключах по умолчанию"
            />
          ) : (
            <Chip size="small" label="Не подключено" />
          )
        }
      />
      <CardContent>
        {usesDefaultCredentials && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Платформа использует общие учётные данные Cloudflare Stream по
            умолчанию. Укажите свои Account ID и API token, чтобы подключить
            собственный аккаунт.
          </Typography>
        )}
        <FormBuilder
          formId="admin-streaming-settings-form"
          schema={schema}
          initialValues={{
            accountId: streamingSettings?.accountId ?? "",
            apiToken: "",
          }}
          onSubmit={async (values) => {
            await updatePlatformStreamingSettings({
              variables: {
                platformId,
                input: {
                  accountId: String(values.accountId),
                  apiToken: String(values.apiToken),
                },
              },
            });
          }}
          submitButton
          resetButton={false}
          submitButtonText="Подключить"
          disabled={loading}
        />
      </CardContent>
    </Card>
  );
};
